import type { ReactNode } from "react";
import type { ResolvedToolDefinition, ToolStep } from "@/data/tools";
import ToolSectionHeader from "./ToolSectionHeader";

type StepsGridProps = {
  steps: readonly ToolStep[];
};

type ToolStepsSectionProps = {
  tool: ResolvedToolDefinition;
  steps?: ToolStep[];
  children?: ReactNode;
};

export default function ToolStepsSection({
  tool,
  steps,
  children,
}: ToolStepsSectionProps) {
  const section = tool.pageContent?.sections?.steps;
  const resolvedSteps = steps ?? tool.pageContent?.steps ?? [];
  const hasSectionCopy = Boolean(section?.title || section?.description);

  if (!resolvedSteps.length && !hasSectionCopy) {
    return null;
  }

  return (
    <section
      className="max-w-4xl mx-auto space-y-8"
      aria-labelledby="howto-heading"
    >
      <ToolSectionHeader
        headingId="howto-heading"
        title={section?.title ?? "How It Works"}
        description={section?.description}
      />
      {resolvedSteps.length ? (
        <StepsGrid steps={resolvedSteps} />
      ) : (
        <div className="rounded-2xl border border-dashed border-[#d7cbf6] bg-gradient-to-br from-white to-[#faf7ff] p-6 text-center text-sm text-gray-500">
          No step items are configured for this tool yet, but the section copy above is still shown.
        </div>
      )}
      {children}
    </section>
  );
}

function StepsGrid({ steps }: StepsGridProps) {
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      {steps.map((step, index) => (
        <div
          key={step.number}
          className={`relative border rounded-2xl p-6 overflow-hidden group hover:border-[#5b32b4]/40 hover:shadow-lg hover:shadow-[#5b32b4]/10 transition-all duration-300 ${
            index % 2 === 0
              ? "bg-gradient-to-br from-white to-[#faf7ff] border-[#e9e1ff]"
              : "bg-gradient-to-br from-white to-[#fff7fb] border-[#f1d8e8]"
          }`}
        >
          <div
            className={`absolute -top-4 -right-4 w-20 h-20 rounded-full transition-all ${
              index % 2 === 0
                ? "bg-[#5b32b4]/5 group-hover:bg-[#5b32b4]/10"
                : "bg-[#fb397d]/10 group-hover:bg-[#fb397d]/15"
            }`}
          />
          <span
            className={`text-5xl font-black leading-none block mb-3 select-none ${
              index % 2 === 0 ? "text-[#f3ecff]" : "text-[#ffe4ef]"
            }`}
          >
            {step.number}
          </span>
          <h3 className="font-bold text-[#2b1d3a] mb-2">{step.title}</h3>
          <p className="text-gray-500 text-sm leading-relaxed">
            {step.description}
          </p>
        </div>
      ))}
    </div>
  );
}
